/**
 * A small Excel (.xlsx) writer with no dependencies: one sheet per dataset,
 * a bold frozen header with filters, sized columns, and real dates, money and
 * percentages. Works in the browser and in Node. The file is a zip of
 * SpreadsheetML parts, deflated where the platform can (CompressionStream).
 */
/** How a column's cells are written. */
export type ExportColumnType = "text" | "number" | "gbp" | "percent" | "date" | "datetime" | "boolean";

export interface XlsxSheet {
  name: string;
  columns: { label: string; type: ExportColumnType }[];
  /** Cells in column order. Dates as YYYY-MM-DD, datetimes as ISO; percent as 0–100. */
  rows: (string | number | boolean | null | undefined)[][];
}

const MAIN = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
const REL = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
const PKG_REL = "http://schemas.openxmlformats.org/package/2006/relationships";
const HEAD = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';

/** Style indexes in styles.xml. */
const STYLE = { text: 0, header: 1, date: 2, datetime: 3, gbp: 4, percent: 5, number: 0 } as const;

const STYLES = `${HEAD}<styleSheet xmlns="${MAIN}">
<numFmts count="3"><numFmt numFmtId="164" formatCode="dd/mm/yyyy"/><numFmt numFmtId="165" formatCode="dd/mm/yyyy hh:mm"/><numFmt numFmtId="166" formatCode="&quot;£&quot;#,##0.00"/></numFmts>
<fonts count="2"><font><sz val="11"/><name val="Calibri"/><family val="2"/></font><font><b/><sz val="11"/><name val="Calibri"/><family val="2"/></font></fonts>
<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>
<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="6">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>
<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="166" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="9" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
</cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

// Characters XML 1.0 can't hold, and lone surrogates.
// eslint-disable-next-line no-control-regex
const INVALID = /[\u0000-\u0008\u000B\u000C\u000E-\u001F￾￿]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g;
const esc = (s: string) => s.replace(INVALID, "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** A, B … Z, AA, AB … */
export function columnName(i: number): string {
  let n = i + 1;
  let s = "";
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

const EPOCH = Date.UTC(1899, 11, 30);
/** Excel's serial day for a calendar date (YYYY-MM-DD). */
function dateSerial(iso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return (Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) - EPOCH) / 86_400_000;
}
/** Excel's serial for an instant, in local time (Excel has no time zones). */
function dateTimeSerial(iso: string): number | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return (Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds()) - EPOCH) / 86_400_000;
}

/** Sheet names: 31 characters at most, none of []:*?/\, and unique in the workbook. */
export function sheetNames(names: string[]): string[] {
  const used = new Set<string>();
  return names.map((raw) => {
    const base = (raw.replace(/[[\]:*?/\\]/g, " ").replace(/\s+/g, " ").replace(/^'+|'+$/g, "").trim() || "Sheet").slice(0, 31);
    let name = base;
    for (let i = 2; used.has(name.toLowerCase()); i++) name = `${base.slice(0, 31 - String(i).length - 1)} ${i}`;
    used.add(name.toLowerCase());
    return name;
  });
}

function textCell(ref: string, value: string, style: number = STYLE.text) {
  const s = value.length > 32_767 ? value.slice(0, 32_767) : value;
  return `<c r="${ref}" t="inlineStr"${style ? ` s="${style}"` : ""}><is><t xml:space="preserve">${esc(s)}</t></is></c>`;
}

function cell(ref: string, type: ExportColumnType, value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined || value === "") return "";
  // Yes/No as text: every spreadsheet app (and Quick Look) shows it, which isn't true of boolean cells.
  if (typeof value === "boolean" || type === "boolean") {
    const yes = typeof value === "boolean" ? value : value === "true" || value === 1;
    return textCell(ref, yes ? "Yes" : "No");
  }
  if (type === "date" || type === "datetime") {
    const serial = typeof value === "string" ? (type === "date" ? dateSerial(value) : dateTimeSerial(value)) : null;
    if (serial === null) return textCell(ref, String(value));
    return `<c r="${ref}" s="${type === "date" ? STYLE.date : STYLE.datetime}"><v>${serial}</v></c>`;
  }
  if (type === "number" || type === "gbp" || type === "percent") {
    const n = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(n)) return textCell(ref, String(value));
    const v = type === "percent" ? n / 100 : n;
    const style = type === "gbp" ? STYLE.gbp : type === "percent" ? STYLE.percent : STYLE.number;
    return `<c r="${ref}"${style ? ` s="${style}"` : ""}><v>${v}</v></c>`;
  }
  return textCell(ref, String(value));
}

/** Column widths from the header and the first rows' content, within sensible bounds. */
function widths(sheet: XlsxSheet): number[] {
  return sheet.columns.map((c, i) => {
    let w = c.label.length + 2;
    for (const row of sheet.rows.slice(0, 200)) {
      const v = row[i];
      const len = c.type === "date" ? 10 : c.type === "datetime" ? 16 : v === null || v === undefined ? 0 : String(v).length;
      w = Math.max(w, Math.min(len, 60) + 2);
    }
    return Math.max(8, Math.min(w, 62));
  });
}

function sheetXml(sheet: XlsxSheet): string {
  const cols = sheet.columns;
  const lastCol = columnName(Math.max(cols.length - 1, 0));
  const lastRow = sheet.rows.length + 1;
  const header = `<row r="1">${cols.map((c, i) => textCell(`${columnName(i)}1`, c.label, STYLE.header)).join("")}</row>`;
  const body = sheet.rows
    .map((row, r) => `<row r="${r + 2}">${cols.map((c, i) => cell(`${columnName(i)}${r + 2}`, c.type, row[i])).join("")}</row>`)
    .join("");
  const colXml = widths(sheet)
    .map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`)
    .join("");
  return `${HEAD}<worksheet xmlns="${MAIN}" xmlns:r="${REL}">
<dimension ref="A1:${lastCol}${lastRow}"/>
<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
<sheetFormatPr defaultRowHeight="15"/>
${cols.length ? `<cols>${colXml}</cols>` : ""}
<sheetData>${header}${body}</sheetData>
${cols.length ? `<autoFilter ref="A1:${lastCol}${lastRow}"/>` : ""}
</worksheet>`;
}

/* ------------------------------------------------------------------ */
/* Zip                                                                 */
/* ------------------------------------------------------------------ */

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

export function crc32(data: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < data.length; i++) c = CRC_TABLE[(c ^ data[i]!) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

async function deflateRaw(data: Uint8Array): Promise<Uint8Array | null> {
  const CS = (globalThis as { CompressionStream?: new (format: string) => { readable: ReadableStream<Uint8Array>; writable: WritableStream<Uint8Array> } }).CompressionStream;
  if (!CS) return null;
  try {
    const source = new ReadableStream<Uint8Array>({
      start(controller) {
        controller.enqueue(data);
        controller.close();
      },
    });
    const stream = source.pipeThrough(new CS("deflate-raw") as unknown as ReadableWritablePair<Uint8Array, Uint8Array>);
    return new Uint8Array(await new Response(stream).arrayBuffer());
  } catch {
    return null;
  }
}

async function zip(files: { name: string; data: Uint8Array }[]): Promise<Uint8Array> {
  const enc = new TextEncoder();
  const chunks: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;
  // 1 Jan 1980, the zip epoch: the hub doesn't need real file times.
  const time = 0;
  const date = (0 << 9) | (1 << 5) | 1;
  for (const f of files) {
    const name = enc.encode(f.name);
    const crc = crc32(f.data);
    const deflated = await deflateRaw(f.data);
    const useDeflate = !!deflated && deflated.length < f.data.length;
    const body = useDeflate ? deflated! : f.data;
    const method = useDeflate ? 8 : 0;

    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true);
    local.setUint16(4, 20, true);
    local.setUint16(6, 0x0800, true);
    local.setUint16(8, method, true);
    local.setUint16(10, time, true);
    local.setUint16(12, date, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, body.length, true);
    local.setUint32(22, f.data.length, true);
    local.setUint16(26, name.length, true);
    local.setUint16(28, 0, true);
    chunks.push(new Uint8Array(local.buffer), name, body);

    const dir = new DataView(new ArrayBuffer(46));
    dir.setUint32(0, 0x02014b50, true);
    dir.setUint16(4, 20, true);
    dir.setUint16(6, 20, true);
    dir.setUint16(8, 0x0800, true);
    dir.setUint16(10, method, true);
    dir.setUint16(12, time, true);
    dir.setUint16(14, date, true);
    dir.setUint32(16, crc, true);
    dir.setUint32(20, body.length, true);
    dir.setUint32(24, f.data.length, true);
    dir.setUint16(28, name.length, true);
    dir.setUint32(42, offset, true);
    central.push(new Uint8Array(dir.buffer), name);
    offset += 30 + name.length + body.length;
  }
  const dirSize = central.reduce((a, c) => a + c.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);
  end.setUint16(8, files.length, true);
  end.setUint16(10, files.length, true);
  end.setUint32(12, dirSize, true);
  end.setUint32(16, offset, true);
  const all = [...chunks, ...central, new Uint8Array(end.buffer)];
  const out = new Uint8Array(all.reduce((a, c) => a + c.length, 0));
  let at = 0;
  for (const c of all) {
    out.set(c, at);
    at += c.length;
  }
  return out;
}

/** The .xlsx file for these sheets. */
export async function buildXlsx(sheets: XlsxSheet[]): Promise<Uint8Array> {
  const list = sheets.length ? sheets : [{ name: "Sheet", columns: [], rows: [] }];
  const names = sheetNames(list.map((s) => s.name));
  const enc = new TextEncoder();
  const n = list.length;
  // Excel names each sheet's filter range; it opens fine without, but tidier with.
  const filterNames = list
    .map((s, i) =>
      s.columns.length
        ? `<definedName name="_xlnm._FilterDatabase" localSheetId="${i}" hidden="1">'${esc(names[i]!.replace(/'/g, "''"))}'!$A$1:$${columnName(s.columns.length - 1)}$${s.rows.length + 1}</definedName>`
        : "",
    )
    .join("");
  const files: { name: string; data: string }[] = [
    {
      name: "[Content_Types].xml",
      data: `${HEAD}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>${list
        .map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`)
        .join("")}</Types>`,
    },
    { name: "_rels/.rels", data: `${HEAD}<Relationships xmlns="${PKG_REL}"><Relationship Id="rId1" Type="${REL}/officeDocument" Target="xl/workbook.xml"/></Relationships>` },
    {
      name: "xl/workbook.xml",
      data: `${HEAD}<workbook xmlns="${MAIN}" xmlns:r="${REL}"><sheets>${names
        .map((name, i) => `<sheet name="${esc(name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`)
        .join("")}</sheets>${filterNames ? `<definedNames>${filterNames}</definedNames>` : ""}</workbook>`,
    },
    {
      name: "xl/_rels/workbook.xml.rels",
      data: `${HEAD}<Relationships xmlns="${PKG_REL}">${list
        .map((_, i) => `<Relationship Id="rId${i + 1}" Type="${REL}/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`)
        .join("")}<Relationship Id="rId${n + 1}" Type="${REL}/styles" Target="styles.xml"/></Relationships>`,
    },
    { name: "xl/styles.xml", data: STYLES },
    ...list.map((s, i) => ({ name: `xl/worksheets/sheet${i + 1}.xml`, data: sheetXml(s) })),
  ];
  return zip(files.map((f) => ({ name: f.name, data: enc.encode(f.data) })));
}
